/**
 * Get user position in a market
 */
import type { Address } from "viem"
import { type ReadableClient, readPrices, readUserPosition } from "../../internal/contracts"
import { fromPriceScaleArray, fromShareScaleArray, fromUsdcScale } from "../../internal/convert"
import type { UserPosition } from "../../types"

export interface GetUserPositionParams {
	market: Address
	user: Address
}

/**
 * Get a user's position in a market
 *
 * Returns shares per bucket, current portfolio value, claimable amount, and claim status.
 *
 * @example
 * ```ts
 * import { getUserPosition } from "@breakout/sdk/actions/read"
 *
 * const position = await getUserPosition(publicClient, {
 *   market: "0x...",
 *   user: "0x...",
 * })
 * console.log(position.shares) // [100, 0] - 100 shares in bucket 0
 * console.log(position.value) // 45.00 - current USDC value
 * ```
 */
export async function getUserPosition(
	client: ReadableClient,
	params: GetUserPositionParams,
): Promise<UserPosition> {
	const { market, user } = params

	// Fetch position and prices in parallel
	const [rawPosition, rawPrices] = await Promise.all([
		readUserPosition(client, market, user),
		readPrices(client, market),
	])

	const shares = fromShareScaleArray(rawPosition.shares)
	const prices = fromPriceScaleArray(rawPrices)

	// Calculate current portfolio value
	let value = 0
	for (let i = 0; i < shares.length; i++) {
		value += (shares[i] ?? 0) * (prices[i] ?? 0)
	}

	return {
		shares,
		value,
		claimable: fromUsdcScale(rawPosition.claimable),
		claimed: rawPosition.claimed,
	}
}
